"use client";

import { useNavigate } from "react-router-dom";
import styles from "../css/consultation-sidebar.module.css";
import { useConsultation } from "../../../../context/ConsultationContext";
import { useAuth } from "../../../../context/AuthContext";
import {
  User,
  FileText,
  Heart,
  Stethoscope,
  FlaskConical,
  ClipboardList,
  List,
  Link,
  Pill,
  CalendarCheck,
  Check,
} from "lucide-react";

const CONSULTATION_STEPS = [
  { id: 1, icon: <User size={18} />, text: "Patient Overview" },
  { id: 2, icon: <FileText size={18} />, text: "Medical & Social History" },
  { id: 3, icon: <Heart size={18} />, text: "Vitals & Observations" },
  { id: 4, icon: <Stethoscope size={18} />, text: "Symptoms & Exam" },
  { id: 5, icon: <FlaskConical size={18} />, text: "Specialized Tests" },
  { id: 6, icon: <ClipboardList size={18} />, text: "Diagnosis & ICD10" },
  { id: 7, icon: <List size={18} />, text: "Problem List" },
  { id: 8, icon: <Link size={18} />, text: "Treatment & Plan" },
  { id: 9, icon: <Pill size={18} />, text: "Prescription" },
  { id: 10, icon: <CalendarCheck size={18} />, text: "Review & Follow-Up" },
];

export default function ConsultationSidebar() {
  const navigate = useNavigate();
  const { currentStep, goToStep, consultationData, endConsultation, getStepDraft } = useConsultation();
  const { user } = useAuth();

  const storedUser = (() => {
    try {
      const raw = localStorage.getItem("user");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  })();

  const userObj = user || storedUser || {};
  const roleId = Number(userObj?.roleid);
  const roleName = String(userObj?.rolename || "").trim().toLowerCase();
  const isNurse = roleId === 6 || roleName === "nurse";
  const isPCDT = roleId === 8; // PCDT(RP) user
  
  // Check if we're in a PCDT consultation flow (came from treatment service)
  const serviceWorkflowData = getStepDraft('serviceWorkflowData', {});
  const hasServiceWorkflowData = Object.keys(serviceWorkflowData).length > 0;
  
  // Check if we specifically came from a treatment service workflow
  const returnedFromWorkflow = consultationData?.returnedFromWorkflow || false;
  
  // PCDT flow only applies when:
  // 1. User is PCDT
  // 2. Has service workflow data (came from treatment service)
  // 3. Currently on Prescription step (step 9)
  // 4. AND we specifically returned from a treatment service workflow
  const isInPCDTFlow = isPCDT && hasServiceWorkflowData && returnedFromWorkflow && currentStep === 9;
  
  const allowedStepIds = isNurse ? new Set([1, 2, 3, 4, 5, 7, 10]) : isInPCDTFlow ? new Set([9]) : null;
  const visibleSteps = allowedStepIds ? CONSULTATION_STEPS.filter((s) => allowedStepIds.has(s.id)) : CONSULTATION_STEPS;

  const visibleStepIds = visibleSteps.map((s) => s.id);
  const currentVisibleIndex = visibleStepIds.indexOf(currentStep);

  const handleStepClick = (stepId) => {
    if (allowedStepIds && !allowedStepIds.has(stepId)) return;
    goToStep(stepId);
    
    // Navigate to the appropriate route based on step
    switch (stepId) {
      case 1:
        navigate("/customer/clinic/patient-overview");
        break;
      case 2:
        navigate("/customer/clinic/medical-social-history");
        break;
      case 3:
        navigate("/customer/clinic/vitals-observations");
        break;
      case 4:
        navigate("/customer/clinic/symptoms-exam");
        break;
      case 5:
        navigate("/customer/clinic/specialized-tests");
        break;
      case 6:
        navigate("/customer/clinic/diagnosis-icd10");
        break;
      case 7:
        navigate("/customer/clinic/problem-list");
        break;
      case 8:
        navigate("/customer/clinic/treatment-plan");
        break;
      case 9:
        navigate("/customer/clinic/prescription");
        break;
      case 10:
        navigate("/customer/clinic/review-follow-up");
        break;
      default:
        console.log("Invalid step:", stepId);
    }
  };

  return (
    <nav className={styles.sidebar}>
      <div className={styles.sidebarHeader}>
        <h3 className={styles.title}>CONSULTATION STEPS</h3>
      </div>

      <div className={styles.sidebarContent}>
        <ul className={styles.stepList}>
          {visibleSteps.map((step) => {
            const stepIndex = visibleStepIds.indexOf(step.id);
            const displayNumber = stepIndex >= 0 ? stepIndex + 1 : step.id;
            const isActive = currentStep === step.id;
            const isCompleted =
              currentVisibleIndex >= 0 && stepIndex >= 0
                ? stepIndex < currentVisibleIndex
                : currentStep > step.id;

            return (
              <li
                key={step.id}
                className={`${styles.stepItem} ${isActive ? styles.active : ""} ${
                  isCompleted ? styles.completed : ""
                }`}
                onClick={() => handleStepClick(step.id)}
              >
                <div className={styles.stepIcon}>
                  {isCompleted ? (
                    <div className={styles.completedIcon}>
                      <Check size={16} />
                    </div>
                  ) : isActive ? (
                    <div className={styles.activeIcon}>{displayNumber}</div>
                  ) : (
                    <div className={styles.pendingIcon}>{step.icon}</div>
                  )}
                </div>
                <span className={styles.stepText}>{step.text}</span>
              </li>
            );
          })}
        </ul>
      </div>
    </nav>
  );
}
